// src/components/NewsList.jsx
import React, { useEffect, useState } from 'react';
import {
    Table, Button, Container, Alert, Pagination, Modal, Form,
    Badge, Row, Col, Card, Spinner, InputGroup
} from 'react-bootstrap';
import { newsAPI } from '../api/wbuoaApi';
import { useAuth } from '../context/AuthContext';
import {
    FaEdit, FaTrash, FaPlus, FaEye, FaEyeSlash, FaFileDownload,
    FaFile, FaSearch, FaFilter, FaNewspaper, FaCalendarAlt, FaFire, FaChartLine
} from 'react-icons/fa';

const NewsList = () => {
    const { user } = useAuth();
    const userData = user?.data || user;
    const isAdmin = String(userData?.role || '').toLowerCase() === 'admin';

    const [news, setNews] = useState([]);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [currentNews, setCurrentNews] = useState(null);
    const [formData, setFormData] = useState({
        title: '',
        content: '',
        status: 'draft',
        created_by: '',
        is_featured: false
    });
    const [documentFile, setDocumentFile] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    const [documentPreview, setDocumentPreview] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedStatus, setSelectedStatus] = useState('all');
    const [selectedFeatured, setSelectedFeatured] = useState('all');
    const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });

    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

    // Style for dark green labels
    const labelStyle = {
        color: '#006400',
        fontWeight: 'bold'
    };

    // Status badge colors
    const statusColors = {
        draft: 'secondary',
        published: 'success',
        archived: 'warning'
    };

    // Fetch news when component mounts or page changes
    useEffect(() => {
        fetchNews(currentPage);
    }, [currentPage]);

    const fetchNews = async (page = 1) => {
        setIsLoading(true);
        setError(null);
        try {
            console.log('Fetching news for page:', page);
            const response = await newsAPI.listNews(page);
            console.log('News API Response:', response);

            // Handle the response structure with data and pagination
            if (response?.data && Array.isArray(response.data)) {
                setNews(response.data);

                // Set pagination info
                if (response.pagination) {
                    setTotalPages(response.pagination.pages || 1);
                    setTotalItems(response.pagination.total || 0);
                    setItemsPerPage(response.pagination.limit || 10);
                }
            } else if (Array.isArray(response)) {
                // Fallback for array response
                setNews(response);
                setTotalPages(1);
                setTotalItems(response.length);
            } else {
                console.warn('Unexpected response format:', response);
                setNews([]);
                setTotalPages(1);
                setTotalItems(0);
            }
        } catch (err) {
            console.error('Fetch news error:', err);
            setError('Failed to fetch news: ' + (err.message || 'Server error'));
        } finally {
            setIsLoading(false);
        }
    };

    // Get stats (using current page data)
    const getStats = () => {
        const total = totalItems;
        const published = news.filter(item => item.status === 'published').length;
        const featured = news.filter(item => item.is_featured === 1).length;
        const drafts = news.filter(item => item.status === 'draft').length;

        // Calculate total views from current page only
        const totalViews = news.reduce((sum, item) => sum + (item.views_count || 0), 0);

        return {
            total,
            published,
            featured,
            drafts,
            totalViews,
            currentCount: news.length
        };
    };

    const stats = getStats();

    // Handle news deletion
    const handleDelete = async (id) => {
        if (!isAdmin) {
            setError('Only administrators can delete news articles.');
            return;
        }

        if (!id) {
            setError('Invalid news identifier');
            return;
        }

        if (!window.confirm('Are you sure you want to delete this news article?')) {
            return;
        }

        setDeletingId(id);
        try {
            console.log('Deleting news with id:', id);
            await newsAPI.deleteNews(id);
            setSuccess('News article deleted successfully');

            // Refresh the list - stay on same page if it still has items, otherwise go to previous page
            if (news.length === 1 && currentPage > 1) {
                setCurrentPage(currentPage - 1);
            } else {
                await fetchNews(currentPage);
            }

            setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
            console.error('Delete error:', err);
            setError(`Failed to delete news article: ${err.message || 'Server error'}`);
        } finally {
            setDeletingId(null);
        }
    };

    // Handle edit button click
    const handleEdit = (newsItem) => {
        if (!isAdmin) {
            setError('Only administrators can edit news articles.');
            return;
        }

        if (!newsItem?.id) {
            setError('Invalid news data');
            return;
        }

        setCurrentNews(newsItem);
        setFormData({
            title: newsItem.title || '',
            content: newsItem.content || '',
            status: newsItem.status || 'draft',
            created_by: newsItem.created_by || '',
            is_featured: newsItem.is_featured === 1
        });
        setDocumentFile(null);
        setDocumentPreview(newsItem.document_path ? {
            name: newsItem.document_path.split('/').pop(),
            path: newsItem.document_path,
            existing: true
        } : null);
        setShowEditModal(true);
    };

    // Handle create new news
    const handleCreate = () => {
        setCurrentNews(null);
        setFormData({
            title: '',
            content: '',
            status: 'draft',
            created_by: '',
            is_featured: false
        });
        setDocumentFile(null);
        setDocumentPreview(null);
        setShowCreateModal(true);
    };

    // Handle news update
    const handleUpdate = async (e) => {
        e.preventDefault();
        if (!isAdmin) {
            setError('Only administrators can edit news articles.');
            return;
        }

        if (!currentNews?.id) {
            setError('No news article selected for update');
            return;
        }

        setIsUpdating(true);
        setError(null);
        try {
            // If there's a new document file, use FormData, otherwise use JSON
            if (documentFile) {
                const formDataToSend = new FormData();
                formDataToSend.append('title', formData.title);
                formDataToSend.append('content', formData.content);
                formDataToSend.append('status', formData.status);
                formDataToSend.append('created_by', formData.created_by || 'admin');
                formDataToSend.append('is_featured', formData.is_featured ? '1' : '0');
                formDataToSend.append('document_path', documentFile);

                await newsAPI.updateNewsWithDocument(currentNews.id, formDataToSend);
            } else {
                // Prepare update data without document
                const updateData = {
                    title: formData.title,
                    content: formData.content,
                    status: formData.status,
                    created_by: formData.created_by || 'admin',
                    is_featured: formData.is_featured ? 1 : 0
                };

                await newsAPI.updateNews(currentNews.id, updateData);
            }

            setShowEditModal(false);
            await fetchNews(currentPage);
            setSuccess('News article updated successfully');
            setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
            console.error('Update error:', err);
            setError(`Failed to update news article: ${err.message || 'Please try again'}`);
        } finally {
            setIsUpdating(false);
        }
    };

    // Handle create new news submission
    const handleCreateSubmit = async (e) => {
        e.preventDefault();
        setIsCreating(true);
        setError(null);

        try {
            // Create FormData for the request
            const formDataToSend = new FormData();

            // Append all text fields
            formDataToSend.append('title', formData.title);
            formDataToSend.append('content', formData.content);
            formDataToSend.append('status', formData.status);
            formDataToSend.append('created_by', formData.created_by || 'admin');
            formDataToSend.append('is_featured', formData.is_featured ? '1' : '0');

            // Append the document file if selected
            if (documentFile) {
                formDataToSend.append('document_path', documentFile);
            }

            // Call the API with the FormData
            await newsAPI.createNews(formDataToSend);

            // Close modal and refresh list - go to first page to see new item
            setShowCreateModal(false);
            setCurrentPage(1);
            await fetchNews(1);
            setSuccess('News article created successfully');

            setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
            console.error('Create error:', err);
            setError(`Failed to create news article: ${err.message || 'Please try again'}`);
        } finally {
            setIsCreating(false);
        }
    };

    // Handle form input changes
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value
        });
    };

    // Handle file input changes
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Check file size (5MB limit)
            if (file.size > 5 * 1024 * 1024) {
                setError('File size must be less than 5MB');
                e.target.value = null;
                return;
            }

            setDocumentFile(file);
            setDocumentPreview({
                name: file.name,
                size: file.size,
                type: file.type,
                existing: false
            });
        }
    };

    // Remove document
    const handleRemoveDocument = () => {
        setDocumentFile(null);
        setDocumentPreview(null);
        // Reset file input
        const fileInput = document.querySelector('input[type="file"]');
        if (fileInput) {
            fileInput.value = '';
        }
    };

    // Handle page change
    const handlePageChange = (page) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Handle next page
    const handleNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    // Handle previous page
    const handlePrevPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    // Handle sort (client-side sorting for current page)
    const handleSort = (key) => {
        const direction = sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
        setSortConfig({ key, direction });

        // Sort the current page data
        const sortedNews = [...news].sort((a, b) => {
            const aValue = a[key];
            const bValue = b[key];

            if (aValue < bValue) {
                return direction === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return direction === 'asc' ? 1 : -1;
            }
            return 0;
        });

        setNews(sortedNews);
    };

    // Format date for display
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Truncate content for table display
    const truncateContent = (content, length = 100) => {
        if (!content) return '';
        return content.length > length ? content.substring(0, length) + '...' : content;
    };

    // Format file size
    const formatFileSize = (bytes) => {
        if (!bytes) return '';
        if (bytes < 1024) return bytes + ' bytes';
        else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
        else return (bytes / 1048576).toFixed(1) + ' MB';
    };

    // Generate pagination items
    const renderPagination = () => {
        if (totalPages <= 1) return null;

        let items = [];
        const maxVisible = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let endPage = Math.min(totalPages, startPage + maxVisible - 1);

        if (endPage - startPage + 1 < maxVisible) {
            startPage = Math.max(1, endPage - maxVisible + 1);
        }

        // Previous button
        items.push(
            <Pagination.Prev
                key="prev"
                onClick={handlePrevPage}
                disabled={currentPage === 1}
            />
        );

        // First page
        if (startPage > 1) {
            items.push(
                <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
                    1
                </Pagination.Item>
            );
            if (startPage > 2) {
                items.push(<Pagination.Ellipsis key="start-ellipsis" />);
            }
        }

        // Page numbers
        for (let i = startPage; i <= endPage; i++) {
            items.push(
                <Pagination.Item
                    key={i}
                    active={i === currentPage}
                    onClick={() => handlePageChange(i)}
                >
                    {i}
                </Pagination.Item>
            );
        }

        // Last page
        if (endPage < totalPages) {
            if (endPage < totalPages - 1) {
                items.push(<Pagination.Ellipsis key="end-ellipsis" />);
            }
            items.push(
                <Pagination.Item
                    key={totalPages}
                    onClick={() => handlePageChange(totalPages)}
                >
                    {totalPages}
                </Pagination.Item>
            );
        }

        // Next button
        items.push(
            <Pagination.Next
                key="next"
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
            />
        );

        return items;
    };

    // Filter functions (client-side filtering for current page)
    const filteredNews = news.filter(item => {
        // Apply status filter
        if (selectedStatus !== 'all' && item.status !== selectedStatus) {
            return false;
        }

        // Apply featured filter
        if (selectedFeatured !== 'all') {
            const isFeatured = selectedFeatured === 'featured';
            if (item.is_featured !== (isFeatured ? 1 : 0)) {
                return false;
            }
        }

        // Apply search filter
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            return (
                item.title?.toLowerCase().includes(term) ||
                item.content?.toLowerCase().includes(term) ||
                item.created_by?.toLowerCase().includes(term)
            );
        }

        return true;
    });

    // Reset filters
    const resetFilters = () => {
        setSearchTerm('');
        setSelectedStatus('all');
        setSelectedFeatured('all');
    };

    return (
        <Container fluid className="py-4">
            {/* Header Section */}
            <Card className="mb-4 border-0 shadow-sm">
                <Card.Body className="p-4">
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <div>
                            <h2 className="mb-1">News Articles Management</h2>
                            <p className="text-muted mb-0">
                                Manage your news articles, track views, and feature important content.
                            </p>
                        </div>
                        <Button variant="primary" onClick={handleCreate} disabled={isLoading}>
                            <FaPlus className="me-2" />
                            Add News
                        </Button>
                    </div>

                    {/* Search and Filter Section */}
                    <Row className="g-3 mb-4">
                        <Col md={4}>
                            <InputGroup>
                                <InputGroup.Text>
                                    <FaSearch />
                                </InputGroup.Text>
                                <Form.Control
                                    placeholder="Search in current page..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                                {searchTerm && (
                                    <Button
                                        variant="outline-secondary"
                                        onClick={() => setSearchTerm('')}
                                    >
                                        Clear
                                    </Button>
                                )}
                            </InputGroup>
                        </Col>
                        <Col md={3}>
                            <Form.Select
                                value={selectedStatus}
                                onChange={(e) => setSelectedStatus(e.target.value)}
                            >
                                <option value="all">All Status</option>
                                <option value="draft">Draft</option>
                                <option value="published">Published</option>
                                <option value="archived">Archived</option>
                            </Form.Select>
                        </Col>
                        <Col md={3}>
                            <Form.Select
                                value={selectedFeatured}
                                onChange={(e) => setSelectedFeatured(e.target.value)}
                            >
                                <option value="all">All Articles</option>
                                <option value="featured">Featured Only</option>
                                <option value="regular">Regular Only</option>
                            </Form.Select>
                        </Col>
                        <Col md={2}>
                            <Button
                                variant="outline-secondary"
                                onClick={resetFilters}
                                className="w-100"
                                title="Reset Filters"
                            >
                                <FaFilter /> Reset
                            </Button>
                        </Col>
                    </Row>

                    {/* Stats Cards */}
                    <Row className="mt-4">
                        <Col md={2}>
                            <Card className="border-0 bg-light">
                                <Card.Body className="py-3">
                                    <div className="d-flex justify-content-between">
                                        <div>
                                            <h6 className="text-muted mb-0">Total</h6>
                                            <h4 className="mb-0">{stats.total}</h4>
                                        </div>
                                        <div className="text-primary">
                                            <FaNewspaper size={24} />
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={2}>
                            <Card className="border-0 bg-light">
                                <Card.Body className="py-3">
                                    <div className="d-flex justify-content-between">
                                        <div>
                                            <h6 className="text-muted mb-0">Published</h6>
                                            <h4 className="mb-0">{stats.published}</h4>
                                        </div>
                                        <div className="text-success">
                                            <FaCalendarAlt size={24} />
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={2}>
                            <Card className="border-0 bg-light">
                                <Card.Body className="py-3">
                                    <div className="d-flex justify-content-between">
                                        <div>
                                            <h6 className="text-muted mb-0">Featured</h6>
                                            <h4 className="mb-0">{stats.featured}</h4>
                                        </div>
                                        <div className="text-warning">
                                            <FaFire size={24} />
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={2}>
                            <Card className="border-0 bg-light">
                                <Card.Body className="py-3">
                                    <div className="d-flex justify-content-between">
                                        <div>
                                            <h6 className="text-muted mb-0">Drafts</h6>
                                            <h4 className="mb-0">{stats.drafts}</h4>
                                        </div>
                                        <div className="text-secondary">
                                            <Badge bg="secondary">Draft</Badge>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={2}>
                            <Card className="border-0 bg-light">
                                <Card.Body className="py-3">
                                    <div className="d-flex justify-content-between">
                                        <div>
                                            <h6 className="text-muted mb-0">Total Views</h6>
                                            <h4 className="mb-0">{stats.totalViews}</h4>
                                        </div>
                                        <div className="text-info">
                                            <FaChartLine size={24} />
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={2}>
                            <Card className="border-0 bg-light">
                                <Card.Body className="py-3">
                                    <div className="d-flex justify-content-between">
                                        <div>
                                            <h6 className="text-muted mb-0">On Page</h6>
                                            <h4 className="mb-0">{stats.currentCount}</h4>
                                        </div>
                                        <div className="text-dark">
                                            <Badge bg="light" text="dark">Page {currentPage}</Badge>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Alerts */}
            {error && (
                <Alert variant="danger" onClose={() => setError(null)} dismissible className="mb-4">
                    <Alert.Heading>Error</Alert.Heading>
                    <p>{error}</p>
                </Alert>
            )}
            {success && (
                <Alert variant="success" onClose={() => setSuccess(null)} dismissible className="mb-4">
                    <Alert.Heading>Success</Alert.Heading>
                    <p>{success}</p>
                </Alert>
            )}

            {/* News Table */}
            <Card className="border-0 shadow-sm">
                <Card.Body className="p-0">
                    {isLoading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3">Loading news articles...</p>
                        </div>
                    ) : (
                        <>
                            <div className="table-responsive">
                                <Table hover className="mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th onClick={() => handleSort('title')} style={{ cursor: 'pointer' }}>
                                                Title {sortConfig.key === 'title' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                                            </th>
                                            <th>Content</th>
                                            <th onClick={() => handleSort('status')} style={{ cursor: 'pointer' }}>
                                                Status {sortConfig.key === 'status' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                                            </th>
                                            <th>Featured</th>
                                            <th>Document</th>
                                            <th onClick={() => handleSort('created_by')} style={{ cursor: 'pointer' }}>
                                                Author {sortConfig.key === 'created_by' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                                            </th>
                                            <th onClick={() => handleSort('created_at')} style={{ cursor: 'pointer' }}>
                                                Created {sortConfig.key === 'created_at' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                                            </th>
                                            <th onClick={() => handleSort('views_count')} style={{ cursor: 'pointer' }}>
                                                Views {sortConfig.key === 'views_count' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                                            </th>
                                            {isAdmin && <th>Actions</th>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredNews.length > 0 ? (
                                            filteredNews.map(item => (
                                                <tr key={item.id}>
                                                    <td>
                                                        <div className="fw-semibold">{item.title}</div>
                                                    </td>
                                                    <td>
                                                        <div className="text-truncate" style={{ maxWidth: '200px' }} title={item.content}>
                                                            {truncateContent(item.content, 80)}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <Badge bg={statusColors[item.status] || 'secondary'}>
                                                            {item.status?.toUpperCase()}
                                                        </Badge>
                                                    </td>
                                                    <td className="text-center">
                                                        {item.is_featured === 1 ? (
                                                            <FaEye className="text-success fs-5" title="Featured" />
                                                        ) : (
                                                            <FaEyeSlash className="text-muted fs-5" title="Not Featured" />
                                                        )}
                                                    </td>
                                                    <td className="text-center">
                                                        {item.document_path ? (
                                                            <a
                                                                href={`${API_BASE_URL}/${item.document_path}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="btn btn-outline-info btn-sm"
                                                                title="View Document"
                                                            >
                                                                <FaFileDownload />
                                                            </a>
                                                        ) : (
                                                            <span className="text-muted">—</span>
                                                        )}
                                                    </td>
                                                    <td>{item.created_by || '—'}</td>
                                                    <td>{formatDate(item.created_at)}</td>
                                                    <td className="text-center">
                                                        <Badge bg="info">{item.views_count || 0}</Badge>
                                                    </td>
                                                    {isAdmin && (
                                                        <td>
                                                            <div className="d-flex gap-2">
                                                                <Button
                                                                    variant="outline-primary"
                                                                    onClick={() => handleEdit(item)}
                                                                    size="sm"
                                                                    title="Edit"
                                                                    aria-label={`Edit ${item.title}`}
                                                                    disabled={deletingId !== null}
                                                                >
                                                                    <FaEdit />
                                                                </Button>
                                                                <Button
                                                                    variant="outline-danger"
                                                                    onClick={() => handleDelete(item.id)}
                                                                    size="sm"
                                                                    title="Delete"
                                                                    aria-label={`Delete ${item.title}`}
                                                                    disabled={deletingId !== null}
                                                                >
                                                                    {deletingId === item.id ? (
                                                                        <Spinner animation="border" size="sm" />
                                                                    ) : (
                                                                        <FaTrash />
                                                                    )}
                                                                </Button>
                                                            </div>
                                                        </td>
                                                    )}
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan={isAdmin ? 9 : 8} className="text-center py-5">
                                                    <div className="py-4">
                                                        <FaSearch size={48} className="text-muted mb-3" />
                                                        <h5>No news articles found</h5>
                                                        <p className="text-muted">
                                                            {searchTerm || selectedStatus !== 'all' || selectedFeatured !== 'all'
                                                                ? 'Try changing your search or filters'
                                                                : 'Add your first news article to get started'}
                                                        </p>
                                                        {searchTerm || selectedStatus !== 'all' || selectedFeatured !== 'all' ? (
                                                            <Button variant="outline-primary" onClick={resetFilters}>
                                                                Reset Filters
                                                            </Button>
                                                        ) : (
                                                            <Button variant="primary" onClick={handleCreate}>
                                                                <FaPlus className="me-2" /> Add News
                                                            </Button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>

                            {/* Pagination */}
                            {totalItems > 0 && totalPages > 0 && (
                                <div className="p-3 border-top">
                                    <div className="d-flex justify-content-between align-items-center flex-wrap">
                                        <div className="text-muted mb-2 mb-md-0">
                                            Showing page {currentPage} of {totalPages} |
                                            Total {totalItems} articles |
                                            {itemsPerPage} per page
                                        </div>
                                        <Pagination className="mb-0">
                                            {renderPagination()}
                                        </Pagination>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </Card.Body>
            </Card>

            {/* Edit News Modal */}
            <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg">
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title>Edit News Article</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleUpdate}>
                        <Form.Group className="mb-3">
                            <Form.Label style={labelStyle}>Title</Form.Label>
                            <Form.Control
                                type="text"
                                name="title"
                                value={formData.title}
                                onChange={handleChange}
                                required
                                disabled={isUpdating}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label style={labelStyle}>Content</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={6}
                                name="content"
                                value={formData.content}
                                onChange={handleChange}
                                required
                                disabled={isUpdating}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label style={labelStyle}>Status</Form.Label>
                            <Form.Select
                                name="status"
                                value={formData.status}
                                onChange={handleChange}
                                disabled={isUpdating}
                            >
                                <option value="draft">Draft</option>
                                <option value="published">Published</option>
                                <option value="archived">Archived</option>
                            </Form.Select>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label style={labelStyle}>Author</Form.Label>
                            <Form.Control
                                type="text"
                                name="created_by"
                                value={formData.created_by}
                                onChange={handleChange}
                                placeholder="admin"
                                disabled={isUpdating}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label style={labelStyle}>Document (Optional)</Form.Label>
                            {documentPreview ? (
                                <div className="d-flex align-items-center justify-content-between p-2 border rounded mb-2">
                                    <div className="d-flex align-items-center">
                                        <FaFile className="me-2 text-primary" />
                                        <div>
                                            <div className="fw-bold">{documentPreview.name}</div>
                                            {documentPreview.size && (
                                                <small className="text-muted">{formatFileSize(documentPreview.size)}</small>
                                            )}
                                            {documentPreview.existing && (
                                                <small className="text-success d-block">Existing document</small>
                                            )}
                                        </div>
                                    </div>
                                    <Button
                                        variant="outline-danger"
                                        size="sm"
                                        onClick={handleRemoveDocument}
                                        disabled={isUpdating}
                                    >
                                        Remove
                                    </Button>
                                </div>
                            ) : (
                                <Form.Control
                                    type="file"
                                    name="document"
                                    onChange={handleFileChange}
                                    accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.jpg,.jpeg,.png"
                                    disabled={isUpdating}
                                />
                            )}
                            <Form.Text className="text-muted">
                                Supported formats: PDF, Word, Excel, Text, Images (Max: 5MB)
                            </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-4">
                            <Form.Check
                                type="checkbox"
                                name="is_featured"
                                label="Featured Article"
                                checked={formData.is_featured}
                                onChange={handleChange}
                                style={labelStyle}
                                disabled={isUpdating}
                            />
                        </Form.Group>

                        <div className="d-flex justify-content-end border-top pt-3">
                            <Button
                                variant="outline-secondary"
                                onClick={() => setShowEditModal(false)}
                                className="me-2"
                                disabled={isUpdating}
                            >
                                Cancel
                            </Button>
                            <Button variant="primary" type="submit" disabled={isUpdating}>
                                {isUpdating ? (
                                    <>
                                        <Spinner
                                            as="span"
                                            animation="border"
                                            size="sm"
                                            role="status"
                                            aria-hidden="true"
                                            className="me-2"
                                        />
                                        Updating...
                                    </>
                                ) : (
                                    'Save Changes'
                                )}
                            </Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>

            {/* Create News Modal */}
            <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} size="lg">
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title>Create New News Article</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleCreateSubmit}>
                        <Form.Group className="mb-3">
                            <Form.Label style={labelStyle}>Title</Form.Label>
                            <Form.Control
                                type="text"
                                name="title"
                                value={formData.title}
                                onChange={handleChange}
                                required
                                disabled={isCreating}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label style={labelStyle}>Content</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={6}
                                name="content"
                                value={formData.content}
                                onChange={handleChange}
                                required
                                disabled={isCreating}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label style={labelStyle}>Status</Form.Label>
                            <Form.Select
                                name="status"
                                value={formData.status}
                                onChange={handleChange}
                                disabled={isCreating}
                            >
                                <option value="draft">Draft</option>
                                <option value="published">Published</option>
                                <option value="archived">Archived</option>
                            </Form.Select>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label style={labelStyle}>Author</Form.Label>
                            <Form.Control
                                type="text"
                                name="created_by"
                                value={formData.created_by}
                                onChange={handleChange}
                                placeholder="admin"
                                disabled={isCreating}
                            />
                            <Form.Text className="text-muted">
                                Default value: admin
                            </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label style={labelStyle}>Document (Optional)</Form.Label>
                            {documentPreview ? (
                                <div className="d-flex align-items-center justify-content-between p-2 border rounded mb-2">
                                    <div className="d-flex align-items-center">
                                        <FaFile className="me-2 text-primary" />
                                        <div>
                                            <div className="fw-bold">{documentPreview.name}</div>
                                            {documentPreview.size && (
                                                <small className="text-muted">{formatFileSize(documentPreview.size)}</small>
                                            )}
                                            {documentPreview.type && (
                                                <small className="text-muted d-block">Type: {documentPreview.type}</small>
                                            )}
                                        </div>
                                    </div>
                                    <Button
                                        variant="outline-danger"
                                        size="sm"
                                        onClick={handleRemoveDocument}
                                        disabled={isCreating}
                                    >
                                        Remove
                                    </Button>
                                </div>
                            ) : (
                                <Form.Control
                                    type="file"
                                    name="document"
                                    onChange={handleFileChange}
                                    accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.jpg,.jpeg,.png"
                                    disabled={isCreating}
                                />
                            )}
                            <Form.Text className="text-muted">
                                Supported formats: PDF, Word, Excel, Text, Images (Max: 5MB)
                            </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-4">
                            <Form.Check
                                type="checkbox"
                                name="is_featured"
                                label="Featured Article"
                                checked={formData.is_featured}
                                onChange={handleChange}
                                style={labelStyle}
                                disabled={isCreating}
                            />
                        </Form.Group>

                        <div className="d-flex justify-content-end border-top pt-3">
                            <Button
                                variant="outline-secondary"
                                onClick={() => setShowCreateModal(false)}
                                className="me-2"
                                disabled={isCreating}
                            >
                                Cancel
                            </Button>
                            <Button variant="primary" type="submit" disabled={isCreating}>
                                {isCreating ? (
                                    <>
                                        <Spinner
                                            as="span"
                                            animation="border"
                                            size="sm"
                                            role="status"
                                            aria-hidden="true"
                                            className="me-2"
                                        />
                                        Creating...
                                    </>
                                ) : (
                                    'Create News Article'
                                )}
                            </Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>
        </Container>
    );
};

export default NewsList;
